/**
 * Mutu Ancak Page
 * Main page for quality inspection report generation
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Flex,
    useToast,
    Button,
    Text,
    VStack,
    Spinner,
    Heading,
} from '@chakra-ui/react';
import { FiDownload } from 'react-icons/fi';
import Navbar from '../../../components/Navbar';
import FilterMutuAncak from '../components/FilterMutuAncak';
import { useMutuAncak } from '../hooks/useMutuAncak';
import { MutuAncakFilters } from '../types';
import { downloadMutuAncakPDF } from '../utils/pdfGenerator';
import { MutuAncakPdfPreview } from '../components/MutuAncakPdfPreview';

const MutuAncakPage: React.FC = () => {
    const toast = useToast();
    const { data, penaltyRates, loading, error, fetchData } = useMutuAncak();

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const [filters, setFilters] = useState<MutuAncakFilters>({
        estate_id: null,
        division_id: null,
        inspection_date: getTodayDate(),
    });

    const [appliedFilters, setAppliedFilters] = useState<MutuAncakFilters>({
        estate_id: null,
        division_id: null,
        inspection_date: getTodayDate(),
    });

    const [hasAppliedFilters, setHasAppliedFilters] = useState(false);

    const handleFilterChange = useCallback((newFilters: MutuAncakFilters) => {
        setFilters(newFilters);
    }, []);

    const handleApplyFilters = useCallback(() => {
        if (!filters.estate_id || !filters.division_id || !filters.inspection_date) {
            toast({
                title: 'Filter tidak lengkap',
                description: 'Silakan pilih Estate, Divisi, dan Tanggal Inspeksi',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setAppliedFilters(filters);
        setHasAppliedFilters(true);
        fetchData(filters);
    }, [filters, fetchData, toast]);

    const handleClearFilters = useCallback(() => {
        const clearedFilters: MutuAncakFilters = {
            estate_id: null,
            division_id: null,
            inspection_date: getTodayDate(),
        };
        setFilters(clearedFilters);
        setAppliedFilters(clearedFilters);
        setHasAppliedFilters(false);
    }, []);

    const handleDownloadPDF = useCallback(() => {
        if (data.length === 0) {
            toast({
                title: 'Tidak ada data',
                description: 'Tidak ada data untuk diekspor ke PDF',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            downloadMutuAncakPDF(data, appliedFilters.inspection_date, penaltyRates);
            toast({
                title: 'Berhasil',
                description: 'PDF berhasil diunduh',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (err) {
            console.error('Error generating PDF:', err);
            toast({
                title: 'Error',
                description: 'Gagal membuat PDF',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    }, [data, appliedFilters.inspection_date, toast, penaltyRates]);

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />
            <Flex 
                h={{ base: 'auto', md: 'calc(100vh - 40px)' }} 
                direction={{ base: 'column', md: 'row' }}>
                {/* Sidebar Filter */}
                <Box
                    w={{ base: '100%', md: '320px' }}
                    bg="white"
                    borderRight={{ base: 'none', md: '1px solid' }}
                    borderBottom={{ base: '1px solid', md: 'none' }}
                    borderColor="gray.200"
                    display="block"
                    overflowY="auto"
                    maxH={{ base: '400px', md: 'none' }}
                >
                    <FilterMutuAncak
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onApplyFilters={handleApplyFilters}
                    />
                </Box>

                {/* Main Content */}
                <Box
                    flex="1"
                    p={{ base: 4, md: 6 }}
                    overflowY="auto"
                >
                    {!hasAppliedFilters ? (
                        <VStack
                            spacing={4}
                            align="center"
                            justify="center"
                            h="100%"
                        >
                            <Text fontSize="lg" color="gray.500">
                                Pilih filter dan klik "Terapkan Filter" untuk melihat data
                            </Text>
                        </VStack>
                    ) : loading ? (
                        <VStack
                            spacing={4}
                            align="center"
                            justify="center"
                            h="100%"
                        >
                            <Spinner size="xl" color="blue.500" />
                            <Text fontSize="lg" color="gray.500">
                                Memuat data...
                            </Text>
                        </VStack>
                    ) : error ? (
                        <VStack
                            spacing={4}
                            align="center"
                            justify="center"
                            h="100%"
                        >
                            <Text fontSize="lg" color="red.500">
                                Error: {error}
                            </Text>
                        </VStack>
                    ) : data.length === 0 ? (
                        <VStack
                            spacing={4}
                            align="center"
                            justify="center"
                            h="100%"
                        >
                            <Text fontSize="lg" color="gray.500">
                                Tidak ada data untuk filter yang dipilih
                            </Text>
                        </VStack>
                    ) : (
                        <VStack spacing={6} align="stretch">
                            {/* PDF Preview Section */}
                            <Box bg="white" p={6} borderRadius="md" boxShadow="sm">
                                <Heading as="h2" size="md" mb={4} color="customGreen.500">
                                    PDF Preview
                                </Heading>
                                <MutuAncakPdfPreview
                                    data={data}
                                    inspectionDate={appliedFilters.inspection_date}
                                    penaltyRates={penaltyRates}
                                    isLoading={loading}
                                />
                            </Box>
                        </VStack>
                    )}
                </Box>
            </Flex>
        </Box>
    );
};

export default MutuAncakPage;